/* Cosmiq hero — one custom element driving everything off a single scroll
   progress value (0 to 1) read from a tall outer container + sticky viewport:
   - video.currentTime scrubbed with the same !video.seeking decoder guard
     used elsewhere on this site (prevents freezes/black frames)
   - headline letters reveal past a threshold
   - rock overlay fades in past a later threshold
   - a CTA button pops in after the headline arrives
   Decorative circles are a one-time entrance triggered when the section
   first comes into view, then loop gently on their own (ambient, not
   scroll-tied, matching the source design). */

(function () {
  'use strict';

  if (customElements.get('cosmiq-hero')) return;

  var REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  class CosmiqHero extends HTMLElement {
    connectedCallback() {
      this.video = this.querySelector('.cosmiq-hero__video');
      this.chars = Array.from(this.querySelectorAll('.cosmiq-hero__char'));
      this.overlay = this.querySelector('.cosmiq-hero__overlay');
      this.cta = this.querySelector('.cosmiq-hero__cta-wrap');
      this.circleInner = this.querySelector('.cosmiq-circle--inner');
      this.circleOuter = this.querySelector('.cosmiq-circle--outer');

      this.buildStars();
      this.primeVideo();

      if (REDUCED_MOTION) {
        if (this.circleInner) this.circleInner.classList.add('is-animated');
        if (this.circleOuter) this.circleOuter.classList.add('is-animated');
        return;
      }

      this.classList.add('cosmiq-hero--armed');

      this.circlesTriggered = false;
      this.circleObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !this.circlesTriggered) {
            this.circlesTriggered = true;
            setTimeout(() => {
              if (this.circleInner) this.circleInner.classList.add('is-animated');
              if (this.circleOuter) this.circleOuter.classList.add('is-animated');
            }, 100);
          }
        });
      });
      this.circleObserver.observe(this);

      if (this.video) {
        this.currentTime = 0;
        this.duration = 0;
        this.video.addEventListener('loadedmetadata', () => {
          this.duration = this.video.duration || 0;
        });
        if (this.video.readyState >= 1) this.duration = this.video.duration || 0;

        // Chain seeks: the moment the decoder finishes one seek, jump
        // straight to the latest smoothed position if it has moved on —
        // without waiting for the next animation frame. Cuts the visible
        // lag between finger and video on phones, where each seek is slow.
        this.video.addEventListener('seeked', () => {
          if (this.seekEpsilon !== undefined &&
              Math.abs(this.video.currentTime - this.currentTime) > this.seekEpsilon) {
            this.video.currentTime = this.currentTime;
          }
        });
      }

      this.lastHeadlineState = false;
      this.lastOverlayState = false;
      this.lastCtaState = false;
      this.rafId = null;
      this.inView = false;

      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          this.inView = entry.isIntersecting;
          if (this.inView) this.startLoop();
        });
      });
      this.observer.observe(this);
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      if (this.circleObserver) this.circleObserver.disconnect();
      if (this.rafId) cancelAnimationFrame(this.rafId);
    }

    primeVideo() {
      // The video keeps the HTML `autoplay` attribute — that's a passive
      // declaration the browser handles itself, and on mobile it's what
      // makes the browser actually download/decode the file at all (many
      // mobile browsers won't preload real video data for a plain <video>
      // with no autoplay, to save data). Crucially, this file's JS never
      // calls .play() itself: a JS-initiated play() that gets blocked by
      // autoplay policy is what makes some mobile browsers show their own
      // "tap to play" button — a passive attribute failing silently does
      // not. Once the browser's own autoplay actually starts, we just
      // .pause() it (always safe, never gated by autoplay policy) so the
      // video sits still until scroll takes over.
      if (!this.video) return;
      var video = this.video;
      var freeze = () => {
        video.pause();
        if (video.currentTime === 0) video.currentTime = 0.01;
      };
      video.addEventListener('playing', freeze, { once: true });
      // If autoplay never starts (blocked entirely), still force a frame
      // decode via seek once metadata is available — never via play().
      var nudge = () => {
        if (video.currentTime === 0 && video.paused) video.currentTime = 0.01;
      };
      if (video.readyState >= 1) {
        nudge();
      } else {
        video.addEventListener('loadedmetadata', nudge, { once: true });
      }
    }

    buildStars() {
      var container = this.querySelector('.cosmiq-hero__stars');
      if (!container) return;
      // Fewer animated layers on phones — 40 infinitely-twinkling elements
      // is fine on desktop GPUs but adds up on mobile during scroll.
      var count = window.matchMedia('(max-width: 767px)').matches ? 18 : 40;
      for (var i = 0; i < count; i++) {
        var star = document.createElement('div');
        star.className = 'cosmiq-star';
        var size = i % 3 === 0 ? 2 : 1;
        star.style.width = size + 'px';
        star.style.height = size + 'px';
        star.style.left = ((i * 37 + 13) % 100) + '%';
        star.style.top = ((i * 53 + 7) % 100) + '%';
        if (!REDUCED_MOTION) {
          star.style.animation = 'cosmiqTwinkle ' + (2 + (i % 4)) + 's ease-in-out ' + ((i * 0.3) % 3) + 's infinite';
        } else {
          star.style.opacity = '0.5';
        }
        container.appendChild(star);
      }
    }

    progress() {
      var rect = this.getBoundingClientRect();
      var scrollable = this.offsetHeight - window.innerHeight;
      if (scrollable <= 0) return 0;
      return Math.min(1, Math.max(0, -rect.top / scrollable));
    }

    startLoop() {
      if (this.rafId) return;
      var tick = () => {
        if (!this.inView) {
          this.rafId = null;
          return;
        }
        this.update();
        this.rafId = requestAnimationFrame(tick);
      };
      this.rafId = requestAnimationFrame(tick);
    }

    update() {
      var progress = this.progress();

      if (this.video && this.duration > 0) {
        var targetTime = progress * this.duration;
        // Catch-up rate: phones get a much snappier factor — their seeks are
        // slow, so gentle smoothing (nice on desktop) reads as the video
        // trailing the finger. Less smoothing = tighter scroll coupling.
        if (this.lerp === undefined) {
          this.lerp = window.matchMedia('(max-width: 767px)').matches ? 0.24 : 0.08;
        }
        this.currentTime += (targetTime - this.currentTime) * this.lerp;
        // Seek threshold = one frame at ~30fps on desktop; phones get a
        // coarser threshold (~2 frames) — their decoders are far slower at
        // seeking, and flooding them is the #1 cause of scroll jank here.
        if (this.seekEpsilon === undefined) {
          this.seekEpsilon = window.matchMedia('(max-width: 767px)').matches ? 0.067 : 0.034;
        }
        if (!this.video.seeking && Math.abs(this.video.currentTime - this.currentTime) > this.seekEpsilon) {
          this.video.currentTime = this.currentTime;
        }
      }

      var showHeadline = progress > 0.45;
      if (showHeadline !== this.lastHeadlineState) {
        this.lastHeadlineState = showHeadline;
        this.chars.forEach((el) => el.classList.toggle('is-revealed', showHeadline));
      }

      var showOverlay = progress > 0.6;
      if (showOverlay !== this.lastOverlayState && this.overlay) {
        this.lastOverlayState = showOverlay;
        this.overlay.classList.toggle('is-visible', showOverlay);
      }

      // Button pops in once the headline has fully arrived (headline
      // starts at 0.45; its staggered letters take a moment to land).
      var showCta = progress > 0.62;
      if (showCta !== this.lastCtaState && this.cta) {
        this.lastCtaState = showCta;
        this.cta.classList.toggle('is-visible', showCta);
      }
    }
  }

  customElements.define('cosmiq-hero', CosmiqHero);
})();
